import { useState, useEffect } from 'react';
import { Button, Space, Typography, Avatar, Alert, App } from 'antd';
import { UserOutlined, DeleteOutlined, CheckOutlined } from '@ant-design/icons';
import { trpc } from '../../lib/api';
import { useOperationStore } from '../../stores/operationStore';
import type { Operator as OperationOperator } from '../../stores/operationStore';
import { withDevModalFilePath } from '../common';

const { Text, Title } = Typography;
const { Modal } = withDevModalFilePath('client/src/components/operation/PasscodeInputModal.tsx');

interface PasscodeInputModalProps {
  visible: boolean;
  operator: {
    id: string;
    name: string;
  };
  sharedUserId: string;
  onSuccess: () => void;
  onCancel: () => void;
}

/**
 * パスコード入力モーダル
 * テンキー風のUIでタッチ操作しやすい
 */
export function PasscodeInputModal({
  visible,
  operator,
  sharedUserId,
  onSuccess,
  onCancel,
}: PasscodeInputModalProps) {
  const { message } = App.useApp();
  const [passcode, setPasscode] = useState('');
  const [error, setError] = useState<string | null>(null);
  const { setOperationMode } = useOperationStore();

  // モーダルが開いたときにパスコードをリセット
  useEffect(() => {
    if (visible) {
      setPasscode('');
      setError(null);
    }
  }, [visible]);

  const startOperationMutation = trpc.userOperator.startOperation.useMutation({
    onSuccess: (data) => {
      if (!data.operator || !data.sessionToken) {
        setError('オペレーター情報の取得に失敗しました');
        setPasscode('');
        return;
      }
      const rawPermissions = data.operator.permissions ?? {};
      const normalizedOperator: OperationOperator = {
        id: data.operator.id ?? operator.id,
        name: data.operator.name ?? operator.name,
        permissions: {
          canCreateSchedule: Boolean(rawPermissions.canCreateSchedule),
          canUpdateSchedule: Boolean(rawPermissions.canUpdateSchedule),
          canCreateWorkHistory: Boolean(rawPermissions.canCreateWorkHistory),
          canUpdateWorkHistory: Boolean(rawPermissions.canUpdateWorkHistory),
          canViewReports: Boolean(rawPermissions.canViewReports),
          canDeleteData: Boolean(rawPermissions.canDeleteData),
          canModifySettings: Boolean(rawPermissions.canModifySettings),
        },
      };
      // オペレーションモードを開始
      setOperationMode(normalizedOperator, data.sessionToken, sharedUserId);
      message.success(`${operator.name}さんでログインしました`);
      onSuccess();
    },
    onError: (error) => {
      setError(error.message);
      setPasscode('');
    },
  });

  const handleNumberClick = (num: number) => {
    if (passcode.length < 10) {
      setPasscode(passcode + num.toString());
      setError(null);
    }
  };

  const handleDelete = () => {
    setPasscode(passcode.slice(0, -1));
    setError(null);
  };

  const handleClear = () => {
    setPasscode('');
    setError(null);
  };

  const handleSubmit = async () => {
    if (passcode.length < 4) {
      setError('パスコードは4桁以上入力してください');
      return;
    }

    try {
      await startOperationMutation.mutateAsync({
        sharedUserId,
        operatorId: operator.id,
        passcode,
      });
    } catch {
      // エラーハンドリングはmutationのonErrorで行う
    }
  };

  // 数字ボタンの配列
  const numberButtons = [
    [1, 2, 3],
    [4, 5, 6],
    [7, 8, 9],
    [0],
  ];

  return (
    <Modal
      title={null}
      open={visible}
      onCancel={onCancel}
      footer={null}
      width={500}
      centered
      closable={true}
    >
      <Space direction="vertical" size="large" style={{ width: '100%', padding: '24px 0' }}>
        {/* オペレーター情報 */}
        <div style={{ textAlign: 'center' }}>
          <Avatar
            size={80}
            icon={<UserOutlined />}
            style={{ background: '#1890ff', marginBottom: 16 }}
          >
            {operator.name[0]}
          </Avatar>
          <Title level={3} style={{ margin: 0 }}>
            {operator.name}
          </Title>
          <Text type="secondary" style={{ fontSize: 16 }}>
            パスコードを入力してください
          </Text>
        </div>

        {/* パスコード表示 */}
        <div
          style={{
            background: '#f5f5f5',
            borderRadius: '8px',
            padding: '24px',
            textAlign: 'center',
            minHeight: '80px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <Space size="large">
            {Array.from({ length: Math.max(4, passcode.length) }).map((_, index) => (
              <div
                key={index}
                style={{
                  width: '20px',
                  height: '20px',
                  borderRadius: '50%',
                  background: index < passcode.length ? '#1890ff' : '#d9d9d9',
                  transition: 'background 0.2s',
                }}
              />
            ))}
          </Space>
        </div>

        {/* エラーメッセージ */}
        {error && (
          <Alert
            message={error}
            type="error"
            showIcon
            style={{ borderRadius: '8px' }}
          />
        )}

        {/* テンキー */}
        <div style={{ width: '100%' }}>
          <Space direction="vertical" size="middle" style={{ width: '100%' }}>
            {numberButtons.map((row, rowIndex) => (
              <Space
                key={rowIndex}
                size="middle"
                style={{
                  width: '100%',
                  justifyContent: row.length === 1 ? 'center' : 'space-between',
                }}
              >
                {row.map((num) => (
                  <Button
                    key={num}
                    size="large"
                    onClick={() => handleNumberClick(num)}
                    style={{
                      width: row.length === 1 ? '120px' : '120px',
                      height: '80px',
                      fontSize: '32px',
                      fontWeight: 'bold',
                      borderRadius: '12px',
                    }}
                    disabled={startOperationMutation.isPending}
                  >
                    {num}
                  </Button>
                ))}
              </Space>
            ))}

            {/* 操作ボタン */}
            <Space size="middle" style={{ width: '100%', justifyContent: 'space-between' }}>
              <Button
                size="large"
                icon={<DeleteOutlined />}
                onClick={handleClear}
                style={{
                  width: '120px',
                  height: '80px',
                  fontSize: '18px',
                  borderRadius: '12px',
                }}
                disabled={startOperationMutation.isPending}
              >
                クリア
              </Button>
              <Button
                size="large"
                icon={<DeleteOutlined />}
                onClick={handleDelete}
                style={{
                  width: '120px',
                  height: '80px',
                  fontSize: '18px',
                  borderRadius: '12px',
                }}
                disabled={startOperationMutation.isPending || passcode.length === 0}
              >
                削除
              </Button>
              <Button
                type="primary"
                size="large"
                icon={<CheckOutlined />}
                onClick={handleSubmit}
                loading={startOperationMutation.isPending}
                style={{
                  width: '120px',
                  height: '80px',
                  fontSize: '18px',
                  borderRadius: '12px',
                }}
                disabled={passcode.length < 4}
              >
                確認
              </Button>
            </Space>
          </Space>
        </div>
      </Space>
    </Modal>
  );
}
